import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  IconButton,
  TextField,
  MenuItem,
  Checkbox,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Add, ArrowBack, Check, Close, Delete, Edit, FileDownload, Print, UploadFile } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import ClearFiltersButton from "../components/ClearFiltersButton";
import { filterFieldSx } from "../theme/filterStyles";
import ResultCount from "../components/ResultCount";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Toast } from "../components/Toast";
import ConfirmModal from "../components/modals/ConfirmModal";
import ImportSheetModal from "../components/modals/ImportSheetModal";
import { entityProfessionalTypeService } from "../services/entityProfessionalTypeService";
import { serviceTypeService, serviceTypeLabel } from "../services/serviceTypeService";
import {
  professionalTeamService,
} from "../services/professionalTeamService";
import {
  professionalServiceValueService,
  ServiceValuesMatrix,
  ServiceValuesMatrixRow,
} from "../services/professionalServiceValueService";
import { colors, pageHeader } from "../theme/designSystem";

interface Option {
  id: string;
  name: string;
}

const fmtMoney = (n?: number | null) =>
  `R$ ${(n ?? 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Máscara de moeda baseada em centavos: dígitos da direita para a esquerda -> "1.234,56".
const maskMoney = (raw: string): string => {
  const digits = raw.replace(/\D/g, "");
  const n = parseInt(digits || "0", 10);
  return (n / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatMoney = (n: number): string =>
  n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const parseMoney = (v: string): number => {
  const n = parseFloat(v.replace(/\./g, "").replace(",", "."));
  return isNaN(n) ? 0 : n;
};

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString("pt-BR") : "-";

/** Linha da tela: a da matriz mais o serviço a que pertence (a lista agrupa por serviço). */
interface MatrixRow extends ServiceValuesMatrixRow {
  serviceTypeId: string;
}

interface RowForm {
  serviceValue: string;
  professionalPaymentVal: string;
  professionalPaymentInc: boolean;
  sessionTimeMinutes: string;
}

// Uma linha em edição por vez: registration da linha + modo (add = criar vínculo novo).
interface EditingState {
  serviceTypeId: string;
  professionalRegistration: string;
  isAdd: boolean;
  form: RowForm;
}

const ServiceProfessionalValues: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const params = getAllParams();
  const entityId = user?.entityId || "";
  const entityName = user?.entityName || "Entidade";
  const initialProfType = params.get("professionalTypeId") || "";
  const initialService = params.get("serviceTypeId") || "";

  const [profTypes, setProfTypes] = useState<Option[]>([]);
  const [services, setServices] = useState<Option[]>([]);
  const [teams, setTeams] = useState<Option[]>([]);
  const [defaultTeam, setDefaultTeam] = useState("");

  const [profTypeFilter, setProfTypeFilter] = useState(initialProfType);
  const [serviceFilter, setServiceFilter] = useState(initialService);
  const [teamFilter, setTeamFilter] = useState("");
  const [professionalFilter, setProfessionalFilter] = useState("");

  const [matrix, setMatrix] = useState<ServiceValuesMatrix | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [savingRow, setSavingRow] = useState(false);
  const [toRemove, setToRemove] = useState<MatrixRow | null>(null);
  const [removing, setRemoving] = useState(false);

  const loadFilterOptions = async () => {
    if (!entityId) return;
    try {
      const data = await entityProfessionalTypeService.getByEntity(entityId);
      setProfTypes(data.linked.map((p) => ({ id: p.id, name: p.name })));
    } catch (error) {
      console.error("Erro ao carregar especialidades:", error);
    }
    try {
      const all = await professionalTeamService.getAll();
      const active = all.filter((t) => t.isActive);
      setTeams(active.map((t) => ({ id: t.id, name: t.title })));
      const main = active.find((t) => t.mainTeam) || active[0];
      if (main) {
        setDefaultTeam(main.id);
        setTeamFilter((current) => current || main.id);
      }
    } catch (error) {
      console.error("Erro ao carregar equipes:", error);
    }
  };

  const loadServices = async (professionalType: string) => {
    if (!entityId || !professionalType) {
      setServices([]);
      return;
    }
    try {
      const svc = await serviceTypeService.getByEntity({
        entityId,
        professionalType,
        active: true,
        pageNumber: 1,
        pageSize: 100,
      });
      setServices(
        // O serviço de convênio leva o plano no rótulo: a mesma descrição se repete um
        // por plano, e sem isso não há como saber qual linha se está precificando (RN-221).
        svc.items.map((s) => ({ id: s.id, name: serviceTypeLabel(s) }))
      );
    } catch (error) {
      console.error("Erro ao carregar serviços:", error);
    }
  };

  // Uma consulta só para os dois casos: serviço escolhido traz um grupo, vazio traz a
  // especialidade inteira — que é o formato para comparar e reajustar (item 4 do pacote).
  const loadRows = async () => {
    if (!teamFilter || !profTypeFilter) {
      setMatrix(null);
      return;
    }
    setLoading(true);
    setEditing(null);
    try {
      setMatrix(await professionalServiceValueService.getMatrix({
        professionalTeam: teamFilter,
        professionalType: profTypeFilter,
        serviceType: serviceFilter || undefined,
      }));
    } catch (error) {
      console.error("Erro ao carregar os valores:", error);
      showToast("Erro ao carregar os valores.", "error");
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${entityName} / Valores por serviço`;
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadServices(profTypeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profTypeFilter]);

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceFilter, teamFilter, profTypeFilter]);

  /** Nome do serviço de UMA linha: com vários grupos na tela, o filtro não serve de fonte. */
  const serviceNameOf = (row: MatrixRow | null) =>
    (matrix?.services ?? []).find((s) => s.serviceTypeId === row?.serviceTypeId)?.description || "";

  // Documento pede equipe e especialidade; o serviço é opcional — sem ele saem TODOS os
  // serviços da especialidade, que é o formato para conferir repasse (pedido do Hilton, 19/09).
  const podeDocumento = !!teamFilter && !!profTypeFilter;

  const documentoParams = {
    professionalTeam: teamFilter,
    professionalType: profTypeFilter,
    serviceType: serviceFilter || undefined,
  };

  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const exportar = async () => {
    if (!podeDocumento || exporting) return;
    setExporting(true);
    try {
      const blob = await professionalServiceValueService.exportMatrixXlsx(documentoParams);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "valores-por-servico.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao exportar valores:", error);
      showToast("Não foi possível gerar a planilha.", "error");
    } finally {
      setExporting(false);
    }
  };

  const imprimir = () => {
    if (!podeDocumento) return;
    const query = new URLSearchParams({
      page: "service-values-print",
      professionalTeam: teamFilter,
      professionalType: profTypeFilter,
      ...(serviceFilter ? { serviceType: serviceFilter } : {}),
    });
    window.open(`${window.location.pathname}?${query.toString()}`, "_blank");
  };

  const startEdit = (row: MatrixRow) => {
    setEditing({
      serviceTypeId: row.serviceTypeId,
      professionalRegistration: row.professionalRegistration,
      isAdd: !row.valueId,
      form: {
        serviceValue:
          row.serviceValue != null ? formatMoney(row.serviceValue) : "0,00",
        professionalPaymentVal:
          row.professionalPaymentVal != null
            ? formatMoney(row.professionalPaymentVal)
            : "0,00",
        professionalPaymentInc: !!row.professionalPaymentInc,
        sessionTimeMinutes:
          row.sessionTimeMinutes != null ? String(row.sessionTimeMinutes) : "0",
      },
    });
  };

  const updateForm = (patch: Partial<RowForm>) =>
    setEditing((e) => (e ? { ...e, form: { ...e.form, ...patch } } : e));

  const handleSaveRow = async (row: MatrixRow) => {
    if (!editing || savingRow) return;
    setSavingRow(true);
    try {
      const payload = {
        professionalTeam: teamFilter,
        // O serviço vem da LINHA, não do filtro: a lista mostra vários grupos ao mesmo tempo
        serviceType: row.serviceTypeId,
        serviceValue: parseMoney(editing.form.serviceValue),
        professionalPaymentVal: parseMoney(editing.form.professionalPaymentVal),
        sessionTimeMinutes: Number(editing.form.sessionTimeMinutes) || 0,
        professionalPaymentInc: editing.form.professionalPaymentInc,
      };
      if (editing.isAdd) {
        await professionalServiceValueService.create(
          row.professionalRegistration,
          payload
        );
      } else if (row.valueId) {
        await professionalServiceValueService.update(row.valueId, payload);
      }
      showToast("Valor de serviço salvo com sucesso.", "success");
      await loadRows();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao salvar. Tente novamente.";
      showToast(message, "error");
    } finally {
      setSavingRow(false);
    }
  };

  const handleRemove = async () => {
    if (!toRemove?.valueId || removing) return;
    setRemoving(true);
    try {
      await professionalServiceValueService.setStatus(toRemove.valueId, false);
      showToast(
        "Serviço removido do profissional (vínculo inativado).",
        "success"
      );
      setToRemove(null);
      await loadRows();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao remover o serviço.";
      showToast(message, "error");
    } finally {
      setRemoving(false);
    }
  };

  // Profissionais elegíveis da especialidade na equipe: saem da própria matriz, sem consulta nova
  const professionalOptions = React.useMemo(() => {
    const byId = new Map<string, string>();
    (matrix?.services ?? []).forEach((servico) =>
      servico.professionals.forEach((p) =>
        byId.set(p.professionalRegistration, p.professionalName || "(sem nome)")));
    return Array.from(byId, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [matrix]);

  const visibleServices = React.useMemo(
    () => (matrix?.services ?? []).map((servico) => ({
      ...servico,
      professionals: professionalFilter
        ? servico.professionals.filter((p) => p.professionalRegistration === professionalFilter)
        : servico.professionals,
    })).filter((servico) => servico.professionals.length > 0),
    [matrix, professionalFilter],
  );

  const totalRows = visibleServices.reduce((acc, s) => acc + s.professionals.length, 0);

  const filtersDirty =
    profTypeFilter !== "" || serviceFilter !== "" || teamFilter !== defaultTeam;

  const isEditingRow = (row: MatrixRow) =>
    editing?.professionalRegistration === row.professionalRegistration
    && editing?.serviceTypeId === row.serviceTypeId;

  /** Uma linha de profissional dentro do grupo do serviço. */
  const renderRow = (row: MatrixRow) => {
            const inEdit = isEditingRow(row);
            const hasValue = !!row.valueId;
            return (
              <Box
                key={row.professionalRegistration}
                className="c4u-table__row"
                sx={{ cursor: inEdit ? "default" : "pointer" }}
              >
                <Box
                  className="admin-plans-cell"
                  sx={{
                    flex: "1 1 auto",
                    minWidth: 180,
                    textAlign: "left",
                    fontWeight: 600,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                  data-label="Profissional"
                >
                  {row.professionalName || "-"}
                </Box>
                <Box className="admin-plans-cell" sx={{ flex: "0 0 140px", textAlign: "left" }} data-label="Vlr. serviço">
                  {inEdit ? (
                    <TextField
                      size="small"
                      value={editing!.form.serviceValue}
                      onChange={(e) =>
                        updateForm({ serviceValue: maskMoney(e.target.value) })
                      }
                      disabled={savingRow}
                      inputProps={{ maxLength: 12, inputMode: "numeric" }}
                      sx={{ width: 110 }}
                    />
                  ) : hasValue ? (
                    fmtMoney(row.serviceValue)
                  ) : (
                    "-"
                  )}
                </Box>
                <Box className="admin-plans-cell" sx={{ flex: "0 0 140px", textAlign: "left" }} data-label="Vlr. profissional">
                  {inEdit ? (
                    <TextField
                      size="small"
                      value={editing!.form.professionalPaymentVal}
                      onChange={(e) =>
                        updateForm({
                          professionalPaymentVal: maskMoney(e.target.value),
                        })
                      }
                      disabled={savingRow}
                      inputProps={{ maxLength: 12, inputMode: "numeric" }}
                      sx={{ width: 110 }}
                    />
                  ) : hasValue ? (
                    fmtMoney(row.professionalPaymentVal)
                  ) : (
                    "-"
                  )}
                </Box>
                <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 110px", textAlign: "left" }} data-label="Rec. indep.">
                  {inEdit ? (
                    <Tooltip title="Recebe independente da realização do serviço agendado (não cancelado pelo profissional)">
                      <Checkbox
                        checked={editing!.form.professionalPaymentInc}
                        disabled={savingRow}
                        disableRipple
                        onChange={(e) =>
                          updateForm({
                            professionalPaymentInc: e.target.checked,
                          })
                        }
                        sx={{
                          p: 0,
                          color: colors.primary,
                          "&.Mui-checked": { color: colors.primary },
                        }}
                      />
                    </Tooltip>
                  ) : hasValue ? (
                    row.professionalPaymentInc ? "sim" : "não"
                  ) : (
                    "-"
                  )}
                </Box>
                <Box className="admin-plans-cell c4u-col--md" sx={{ flex: "0 0 110px", textAlign: "left" }} data-label="Sessão (min.)">
                  {inEdit ? (
                    <TextField
                      size="small"
                      value={editing!.form.sessionTimeMinutes}
                      onChange={(e) =>
                        updateForm({
                          sessionTimeMinutes: e.target.value.replace(/\D/g, ""),
                        })
                      }
                      disabled={savingRow}
                      inputProps={{ maxLength: 4, inputMode: "numeric" }}
                      sx={{ width: 80 }}
                    />
                  ) : hasValue ? (
                    `${row.sessionTimeMinutes ?? 0} min.`
                  ) : (
                    "-"
                  )}
                </Box>
                <Box className="admin-plans-cell c4u-col--xl" sx={{ flex: "0 0 95px", textAlign: "left" }} data-label="Alteração">
                  {hasValue ? formatDate(row.updatedAt) : "-"}
                </Box>
                <Box
                  className="admin-plans-cell admin-plans-actions"
                  sx={{ flex: "0 0 100px", textAlign: "right" }}
                  data-label="Ações"
                >
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    {inEdit ? (
                      <>
                        <Tooltip title="Salvar">
                          <span>
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={() => handleSaveRow(row)}
                              disabled={savingRow}
                              sx={{
                                backgroundColor: "#28a745",
                                color: "white",
                                "&:hover": { backgroundColor: "#218838" },
                                "&.Mui-disabled": {
                                  backgroundColor: "#94d3a2",
                                  color: "white",
                                },
                              }}
                            >
                              {savingRow ? (
                                <CircularProgress size={16} sx={{ color: "white" }} />
                              ) : (
                                <Check sx={{ fontSize: "1rem" }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancelar">
                          <span>
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={() => setEditing(null)}
                              disabled={savingRow}
                              sx={{
                                backgroundColor: "#adb5bd",
                                color: "white",
                                "&:hover": { backgroundColor: "#98a0a8" },
                              }}
                            >
                              <Close sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : hasValue ? (
                      <>
                        <Tooltip title="Editar valores">
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => { e.stopPropagation(); startEdit(row); }}
                            sx={{
                              backgroundColor: "#03B4C6",
                              color: "white",
                              "&:hover": { backgroundColor: "#029AAB" },
                            }}
                          >
                            <Edit sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover o serviço do profissional (inativa o vínculo)">
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setToRemove(row); }}
                            sx={{
                              backgroundColor: "#dc3545",
                              color: "white",
                              "&:hover": { backgroundColor: "#c82333" },
                            }}
                          >
                            <Delete sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title="Adicionar este serviço para o profissional">
                        <IconButton
                          className="c4u-row-action"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); startEdit(row); }}
                          sx={{
                            backgroundColor: "#28a745",
                            color: "white",
                            "&:hover": { backgroundColor: "#218838" },
                          }}
                        >
                          <Add sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <div className="c4u-page-header c4u-page-header--in-panel">
            {/* Desde 19/09/2026 esta tela saiu do menu: a porta é o botão de Tipos de serviço,
                então ela é página-filha e leva o Voltar. */}
            <IconButton
              className="c4u-page-header__back"
              onClick={() => navigateTo("entity-service-types", { entityId, entityName })}
              title="Voltar para Tipos de serviço"
              size="small"
            >
              <ArrowBack />
            </IconButton>
            <div className="c4u-page-header__texts">
              <h1 className="c4u-page-header__title">{entityName} / Valores por serviço</h1>
              <p className="c4u-page-header__subtitle">Valores e repasses de cada serviço da especialidade, por profissional da equipe.</p>
            </div>
            <div className="c4u-page-header__actions">
              <FaqButton />
            </div>
          </div>

          <div className="c4u-panel">
          {/* Filtros: especialidade -> serviço; equipe define as linhas */}
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
              <TextField
                select
                size="small"
                label="Equipe"
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                {teams.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Especialidade"
                value={profTypeFilter}
                onChange={(e) => {
                  setProfTypeFilter(e.target.value);
                  setServiceFilter("");
                  setProfessionalFilter("");
                }}
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {profTypes.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Profissional"
                value={professionalFilter}
                onChange={(e) => setProfessionalFilter(e.target.value)}
                disabled={!profTypeFilter}
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Todos</MenuItem>
                {professionalOptions.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Serviço"
                value={serviceFilter}
                onChange={(e) => setServiceFilter(e.target.value)}
                disabled={!profTypeFilter}
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Todos</MenuItem>
                {services.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton
                  disabled={!filtersDirty}
                  onClick={() => {
                    setProfTypeFilter("");
                    setServiceFilter("");
                    setProfessionalFilter("");
                    setTeamFilter(defaultTeam);
                  }}
                />
                {/* Ação de tela na barra de filtros = contornado, sem cor inventada
                    (o CSS do padrão registra o verde e o ciano cheio como erro anterior). */}
                <Tooltip title="Exportar para Excel">
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={exportar}
                      disabled={!podeDocumento || exporting}
                    >
                      {exporting ? <CircularProgress size={18} /> : <FileDownload />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Importar a planilha exportada, com os valores editados">
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={() => setImportOpen(true)}
                      disabled={!teamFilter}
                    >
                      <UploadFile />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Imprimir">
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={imprimir}
                      disabled={!podeDocumento}
                    >
                      <Print />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

          {/* Item 5: o que está na tela, por extenso — o combo corta, esta linha não. */}
          {matrix && (
            <div className="c4u-context-line">
              {[matrix.teamName,
                matrix.professionalTypeName,
                professionalFilter
                  ? professionalOptions.find((p) => p.id === professionalFilter)?.name
                  : null,
                serviceFilter
                  ? visibleServices[0]?.description
                  : `Todos os serviços (${visibleServices.length})`,
              ].filter(Boolean).join(" › ")}
            </div>
          )}

          <ResultCount shown={totalRows} />
          <Box className="admin-plans-list-container" sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
            <Box className="admin-plans-table admin-entities-table">
              <Box
                className="c4u-table__header">
                <Box className="admin-plans-header-cell" sx={{ flex: "1 1 auto", minWidth: 180, textAlign: "left" }}>Profissional</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 140px", textAlign: "left" }}>Vlr. serviço</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 140px", textAlign: "left" }}>Vlr. profissional</Box>
                <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 110px", textAlign: "left" }}>Rec. indep.</Box>
                <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 110px", textAlign: "left" }}>Sessão (min.)</Box>
                <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 95px", textAlign: "left" }}>Alteração</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px", justifyContent: "flex-end" }}>Ações</Box>
              </Box>

              <Box className="admin-plans-table-body">
                {!profTypeFilter || !teamFilter ? (
                  <Box sx={{ py: 6, textAlign: "center", color: colors.textSecondary }}>
                    Selecione a equipe e a especialidade. Sem escolher serviço, aparecem
                    todos os serviços da especialidade.
                  </Box>
                ) : loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                    <CircularProgress sx={{ color: colors.primary }} />
                  </Box>
                ) : totalRows === 0 ? (
                  <Box sx={{ py: 6, textAlign: "center", color: colors.textSecondary }}>
                    Nenhum profissional desta especialidade na equipe selecionada.
                  </Box>
                ) : (
                  visibleServices.map((servico) => (
                    <React.Fragment key={servico.serviceTypeId}>
                      {/* Cabeçalho do grupo: o serviço identificado por inteiro, para a linha
                          abaixo não depender do filtro (hierarquia da tela, 19/09). */}
                      <Box className="c4u-group-row">
                        <strong>{servico.description}</strong>
                        {servico.origin === "operator" && (
                          <span className="c4u-group-row__detail">
                            {[servico.healthPlanName,
                              servico.procedureCode ? `TUSS ${servico.procedureCode}` : null]
                              .filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </Box>
                      {servico.professionals.map((p) =>
                        renderRow({ ...p, serviceTypeId: servico.serviceTypeId }))}
                    </React.Fragment>
                  ))
                )}
              </Box>
            </Box>
          </Box>
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      <ImportSheetModal
        open={importOpen}
        title="Importar valores"
        hint="Exporte a planilha, altere Valor e Repasse no Excel e suba aqui. Linha em branco não muda o que está gravado; as duas colunas de ID no fim são o que liga cada linha ao serviço e ao profissional — não apague."
        onSend={(file, dryRun) => professionalServiceValueService.importMatrix(file, teamFilter, dryRun)}
        onClose={() => setImportOpen(false)}
        onDone={(r) => {
          showToast(`${r.created} valor(es) incluído(s), ${r.updated} atualizado(s).`, "success");
          loadRows();
        }}
      />

      <ConfirmModal
        isOpen={!!toRemove}
        title="Remover serviço do profissional"
        message={`Deseja remover o serviço "${serviceNameOf(toRemove)}" de ${
          toRemove?.professionalName || "este profissional"
        }? O vínculo será inativado e sairá desta tela.`}
        confirmButtonText="Remover"
        onConfirm={handleRemove}
        onCancel={() => setToRemove(null)}
        type="danger"
        isProcessing={removing}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default ServiceProfessionalValues;
